// descontosSection/index.ts
//
// Folha única das duas abas de "Descontos e Taxas" (Descontos.tsx e Taxas.tsx).
// As duas telas têm a MESMA anatomia — aviso grande no topo, um cartão por
// turno de caixa, tabela do que está gravado e um formulário embaixo —, então
// duplicar dois arquivos quase idênticos só criaria duas verdades sobre o mesmo
// visual.
//
// Anatomia copiada de storesSection/MeiosPagamento.ts, que é o molde bom do
// repo. Tamanhos só na escada de tokens (`var(--sp-N)` / `var(--fs-N)`); o que
// não cai nos valores válidos de --sp vira `calc(var(--u) * N)`.
//
// Cores só de `props.theme.colors`, que hoje tem 5 chaves reais (background,
// text, selection, modalColor, primary). `colors.borderColor` resolve undefined
// no tema atual — é a dívida vigente do repo, herdada aqui com fallback
// explícito em vez de consertada neste card.
import styled, { keyframes } from "styled-components";

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// ===== CONTAINER PRINCIPAL =====
export const FieldsContainer = styled.div`
  width: 100%;
  max-width: 100%;
  height: 85%;
  max-height: 85%;
  padding: var(--sp-24);
  flex-direction: column;
  box-sizing: border-box;
  overflow: auto;

  &::-webkit-scrollbar {
    width: calc(var(--u) * 15);
    height: calc(var(--u) * 15);
  }

  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.colors.modalColor + "90"};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.theme.colors.selection};
    border-radius: 10px;
    border: 2px solid ${(props) => props.theme.colors.modalColor + "90"};
  }
`;

// ===== CABEÇALHO =====
export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--sp-20);
  gap: var(--sp-16);
  flex-wrap: wrap;
  animation: ${fadeIn} 0.3s ease;
`;

export const HeaderContent = styled.div`
  flex: 1;
  min-width: calc(var(--u) * 260);
`;

export const HeaderTitle = styled.h1`
  display: flex;
  align-items: center;
  gap: var(--sp-12);
  margin: 0 0 var(--sp-6) 0;
  font-size: var(--fs-3xl);
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
`;

export const HeaderSubtitle = styled.p`
  margin: 0;
  font-size: var(--fs-base);
  color: ${(props) => props.theme.colors.label};
`;

// ===== AVISO GRANDE (o que esta tela NÃO faz) =====
// Deliberadamente grande, com borda grossa e sem estado de "fechar": o dono
// precisa ler que a regra cadastrada aqui não corrige a aritmética do cupom.
// Um aviso que dá para dispensar é um aviso que ninguém lê duas vezes.
export const AvisoGrande = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--sp-12);
  margin-bottom: var(--sp-20);
  padding: var(--sp-20);
  border-radius: 12px;
  border: 2px solid #8f5e07;
  background-color: #8f5e0722;
  color: ${(props) => props.theme.colors.text};
  font-size: var(--fs-base);
  line-height: 1.5;
  animation: ${fadeIn} 0.3s ease;
`;

export const AvisoGrandeTitulo = styled.div`
  font-size: var(--fs-lg);
  font-weight: 700;
  margin-bottom: var(--sp-6);
`;

// ===== AVISO INLINE =====
// Vive aqui, na própria aba, e não no <MessageBox /> do Zustand: o MessageBox
// não está montado na tela de Configuração (só em Login e Register), então
// setMessageZustand não apareceria para ninguém.
export const Aviso = styled.div<{ $tom: "ok" | "erro" | "atencao" }>`
  margin-top: var(--sp-12);
  padding: var(--sp-10) var(--sp-16);
  border-radius: 8px;
  font-size: var(--fs-md);
  line-height: 1.4;
  border: 1px solid
    ${(props) =>
      props.$tom === "erro"
        ? "#be3028"
        : props.$tom === "atencao"
        ? "#8f5e07"
        : "#26792f"};
  background-color: ${(props) =>
    props.$tom === "erro"
      ? "#be302818"
      : props.$tom === "atencao"
      ? "#8f5e0718"
      : "#26792f18"};
  color: ${(props) => props.theme.colors.text};
  animation: ${fadeIn} 0.3s ease;
`;

export const AvisoLista = styled.ul`
  margin: var(--sp-6) 0 0 0;
  padding-left: var(--sp-20);
`;

// ===== CARTÃO DE UM TURNO DE CAIXA =====
export const Card = styled.div`
  background-color: ${(props) => props.theme.colors.modalColor};
  border: 1px solid ${(props) => props.theme.colors.selection + "40"};
  border-radius: 12px;
  padding: var(--sp-20);
  margin-bottom: var(--sp-20);
  animation: ${fadeIn} 0.3s ease;
`;

export const CardTitulo = styled.h3`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  margin: 0;
  font-size: var(--fs-lg);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
`;

export const CardSubtitulo = styled.div`
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
  margin-top: var(--sp-4);
  margin-bottom: var(--sp-16);
`;

// ===== BLOCO INTERNO =====
export const Bloco = styled.div`
  background-color: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.selection + "30"};
  border-radius: 8px;
  padding: var(--sp-16);
  margin-bottom: var(--sp-16);
`;

export const BlocoTitulo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  font-size: var(--fs-md);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: var(--sp-12);
`;

// ===== FORMULÁRIO =====
export const Grade = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(calc(var(--u) * 220), 1fr));
  gap: var(--sp-12);
`;

export const Campo = styled.div<{ $largo?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  grid-column: ${(props) => (props.$largo ? "1 / -1" : "auto")};
`;

export const Rotulo = styled.label`
  font-size: var(--fs-sm);
  font-weight: 600;
  color: ${(props) => props.theme.colors.label};
`;

export const Dica = styled.div`
  font-size: var(--fs-xs);
  line-height: 1.4;
  color: ${(props) => props.theme.colors.label};
`;

export const Entrada = styled.input`
  padding: var(--sp-8) var(--sp-10);
  border-radius: 6px;
  border: 1px solid
    ${(props) => props.theme.colors.borderColor || "#e0e0e0"};
  background-color: ${(props) => props.theme.colors.modalColor};
  color: ${(props) => props.theme.colors.text};
  font-size: var(--fs-md);
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.selection};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const Seletor = styled.select`
  padding: var(--sp-8) var(--sp-10);
  border-radius: 6px;
  border: 1px solid
    ${(props) => props.theme.colors.borderColor || "#e0e0e0"};
  background-color: ${(props) => props.theme.colors.modalColor};
  color: ${(props) => props.theme.colors.text};
  font-size: var(--fs-md);
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

// Prefixo/sufixo do valor: "%" e "R$" moram FORA do campo de texto, colados
// nele. O defeito de produção é justamente ter "3" e "10%" na mesma coluna —
// aqui a unidade é a leitura do campo "forma", nunca um caractere digitado.
export const CampoComUnidade = styled.div`
  display: flex;
  align-items: stretch;
  gap: var(--sp-6);
`;

export const Unidade = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0 var(--sp-10);
  border-radius: 6px;
  background-color: ${(props) => props.theme.colors.selection + "25"};
  color: ${(props) => props.theme.colors.text};
  font-size: var(--fs-md);
  font-weight: 600;
  white-space: nowrap;
`;

export const Linha = styled.label<{ $desabilitada?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  padding: var(--sp-6) 0;
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.text};
  cursor: ${(props) => (props.$desabilitada ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.$desabilitada ? 0.5 : 1)};
`;

export const Caixa = styled.input`
  width: var(--sp-16);
  height: var(--sp-16);
  cursor: inherit;
  accent-color: ${(props) => props.theme.colors.selection};
`;

export const GrupoRadio = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-16);
`;

// ===== ETIQUETA DE PADRÃO APLICADO =====
// O texto de onde o padrão veio anda junto do padrão: um valor que aparece
// sozinho no campo vira "o sistema decidiu", e ninguém sabe se pode mexer.
export const Etiqueta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--sp-4);
  padding: var(--sp-2) var(--sp-8);
  border-radius: 999px;
  background-color: ${(props) => props.theme.colors.selection + "25"};
  color: ${(props) => props.theme.colors.text};
  font-size: var(--fs-xs);
  font-weight: 600;
`;

// ===== TABELA DO QUE ESTÁ GRAVADO =====
export const Tabela = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.text};
`;

export const TabelaCabecalho = styled.th`
  text-align: left;
  padding: var(--sp-6) var(--sp-8);
  font-size: var(--fs-sm);
  font-weight: 700;
  color: ${(props) => props.theme.colors.label};
  border-bottom: 1px solid ${(props) => props.theme.colors.selection + "40"};
  white-space: nowrap;
`;

export const TabelaCelula = styled.td`
  padding: var(--sp-6) var(--sp-8);
  border-bottom: 1px solid ${(props) => props.theme.colors.selection + "20"};
  vertical-align: top;
`;

export const TabelaAcao = styled.button`
  display: inline-flex;
  align-items: center;
  gap: var(--sp-4);
  padding: var(--sp-4) var(--sp-8);
  border-radius: 6px;
  border: 1px solid ${(props) => props.theme.colors.selection + "60"};
  background: transparent;
  color: ${(props) => props.theme.colors.text};
  font-size: var(--fs-sm);
  cursor: pointer;

  &:hover {
    background-color: ${(props) => props.theme.colors.selection + "25"};
  }
`;

// ===== RODAPÉ DE AÇÃO =====
export const AcaoRodape = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-12);
  flex-wrap: wrap;
  margin-top: var(--sp-16);
  padding-top: var(--sp-16);
  border-top: 1px solid ${(props) => props.theme.colors.selection + "30"};
`;

export const Button = styled.button<{ color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-10) var(--sp-20);
  border-radius: 8px;
  border: none;
  background-color: ${(props) => props.color || props.theme.colors.selection};
  color: ${(props) => props.theme.colors.textOnPrimary};
  font-weight: 600;
  font-size: var(--fs-md);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  gap: var(--sp-6);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${(props) => props.theme.colors.selection + "40"};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    transform: none;
  }
`;

export const ButtonSecundario = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-6);
  padding: var(--sp-10) var(--sp-20);
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.colors.selection + "60"};
  background: transparent;
  color: ${(props) => props.theme.colors.text};
  font-weight: 600;
  font-size: var(--fs-md);
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: ${(props) => props.theme.colors.selection + "20"};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

// ===== ESTADOS =====
export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--sp-16);
  padding: calc(var(--u) * 60);
  color: ${(props) => props.theme.colors.label};
`;

export const LoadingSpinner = styled.div`
  width: var(--sp-40);
  height: var(--sp-40);
  border: 4px solid ${(props) => props.theme.colors.selection + "30"};
  border-top-color: ${(props) => props.theme.colors.selection};
  border-radius: 50%;
  animation: ${spin} 0.9s linear infinite;
`;

export const EmptyState = styled.div`
  border: 2px dashed ${(props) => props.theme.colors.selection + "50"};
  border-radius: 12px;
  padding: calc(var(--u) * 60) var(--sp-40);
  text-align: center;
  animation: ${fadeIn} 0.4s ease;
`;

export const EmptyStateIcon = styled.div`
  font-size: var(--fs-7xl);
  margin-bottom: var(--sp-16);
  animation: ${pulse} 2s ease-in-out infinite;
`;

export const EmptyStateTitle = styled.div`
  font-size: var(--fs-xl);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: var(--sp-8);
`;

export const EmptyStateDescription = styled.div`
  font-size: var(--fs-base);
  color: ${(props) => props.theme.colors.label};
`;

export const VazioNaTabela = styled.div`
  padding: var(--sp-12);
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
`;

export const MaterialIcons = styled.span`
  font-family: "Material Icons";
  font-weight: normal;
  font-style: normal;
  font-size: var(--fs-xl);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;
